import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { VideoCompressionOptions, CompressionResult } from './types';
import { getPreset } from './presets';

export class VideoCompressor {
  private ffmpeg: FFmpeg;
  private options: VideoCompressionOptions;

  constructor(options: VideoCompressionOptions = {}) {
    this.ffmpeg = new FFmpeg();
    this.options = {
      preset: 'web',
      codec: 'h264',
      crf: 28,
      ...options
    };

    // Apply preset settings if specified
    if (this.options.preset && this.options.preset !== 'custom') {
      const preset = getPreset(this.options.preset);
      if (preset?.video) {
        this.options = { ...preset.video, ...this.options };
      }
    }

    this.setupFFmpeg();
  }

  private async setupFFmpeg(): Promise<void> {
    try {
      // Setup progress listener
      this.ffmpeg.on('progress', ({ progress }) => {
        const percentage = Math.round(progress * 100);
        this.options.onProgress?.(percentage);
      });

      this.ffmpeg.on('log', ({ message }) => {
        console.log('[FFmpeg]', message);
      });

      // Load FFmpeg
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
      await this.ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
    } catch (error) {
      console.error('Failed to load FFmpeg:', error);
      this.options.onError?.(error as Error);
    }
  }

  async compress(file: File): Promise<CompressionResult> {
    const startTime = performance.now();
    
    try {
      // Ensure FFmpeg is loaded
      if (!this.ffmpeg.loaded) {
        await this.setupFFmpeg();
      }

      // Write input file
      await this.ffmpeg.writeFile(file.name, await fetchFile(file));

      // Build FFmpeg command
      const outputName = `compressed_${file.name.replace(/\.[^/.]+$/, '')}.mp4`;
      const command = this.buildFFmpegCommand(file.name, outputName);

      // Execute compression
      await this.ffmpeg.exec(command);

      // Read output file
      const data = await this.ffmpeg.readFile(outputName) as Uint8Array;
      const blob = new Blob([data.buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);

      // Calculate metrics
      const originalSize = file.size;
      const compressedSize = blob.size;
      const compressionRatio = Math.round(((originalSize - compressedSize) / originalSize) * 100);
      const processingTime = performance.now() - startTime;

      const result: CompressionResult = {
        originalSize,
        compressedSize,
        compressionRatio,
        processingTime,
        blob,
        url
      };

      this.options.onComplete?.(result);
      return result;

    } catch (error) {
      this.options.onError?.(error as Error);
      throw error;
    }
  }

  private buildFFmpegCommand(inputName: string, outputName: string): string[] {
    const command = ['-i', inputName];

    // Video codec
    command.push('-c:v', this.options.codec || 'h264');

    // CRF (quality)
    if (this.options.crf !== undefined) {
      command.push('-crf', this.options.crf.toString());
    }

    // Resolution
    if (this.options.resolution) {
      const { width, height } = this.options.resolution;
      command.push('-vf', `scale=${width}:${height}`);
    }

    // Framerate
    if (this.options.framerate) {
      command.push('-r', this.options.framerate.toString());
    }

    // Video bitrate
    if (this.options.bitrate) {
      command.push('-b:v', this.options.bitrate);
    }

    // Audio settings
    if (this.options.audio) {
      if (this.options.audio.codec) {
        command.push('-c:a', this.options.audio.codec);
      }
      if (this.options.audio.bitrate) {
        command.push('-b:a', this.options.audio.bitrate);
      }
      if (this.options.audio.sampleRate) {
        command.push('-ar', this.options.audio.sampleRate.toString());
      }
    }

    // Output settings
    command.push('-preset', 'medium');
    command.push('-movflags', '+faststart');
    command.push('-y'); // Overwrite output file
    command.push(outputName);

    return command;
  }

  async terminate(): Promise<void> {
    await this.ffmpeg.terminate();
  }
}
